import React, { useMemo, useState, useEffect, useRef } from 'react';
import '../styles/ProjectSettings.css';
import ProjectService from '../services/projectService';
import { useAuth } from '../auth';
import { graphqlRequest } from '../services/graphqlClient.js';
import AddressAutocomplete from './AddressAutocomplete';
import { companyMembersByCompany } from '../graphql/queries.js';
import { projectMembersByProjectLite, classifiedDocumentsByProjectBuildingCode, classifiedDocumentsByProjectWithTags, updateClassifiedDocumentTags } from '../graphql/customQueries.js';
import { useNavigate } from 'react-router-dom';
import StandardDropdown from './compliance-ui/StandardDropdown.jsx';
import { createProjectMember, deleteProjectMember } from '../graphql/mutations.js';
import { jwtDecode } from 'jwt-decode';
import { OCCUPANCY_TYPES_US } from '../constants/occupancyTypes_US';
import { OCCUPANCY_TYPES_CA } from '../constants/occupancyTypes_CA';
import { CONSTRUCTION_TYPES_CA } from '../constants/constructionTypes_CA';
import { CONSTRUCTION_TYPES_US } from '../constants/constructionTypes_US';
import { useAlertDialog } from '../contexts/AlertDialogContext';
import { phConstructionTypeSet } from '../utils/posthogEvents';
import { planHasTeamAccess } from '../utils/userAccess';
import { buildProjectContext } from '../utils/projectContext';
import DatePicker from './DatePicker';
import {
  formatWithCommas,
  removeCommas,
  onlyDigits,
  onlyDecimal,
  isPart9Canada,
  PROJECT_PHASE_OPTIONS,
  PROJECT_STATUS_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  TEAM_ROLE_OPTIONS,
} from '../constants/projectFieldsConfig';
import {
  DEFAULT_PROJECT_CATEGORY,
  PROJECT_CATEGORY_OPTIONS,
  getProjectCategoryProfile,
  isCivilCategory,
  isFieldVisible,
} from '../constants/projectCategoryProfiles';
import { CIVIL_VERTICAL_OPTIONS, CIVIL_VERTICALS_SELECTION_HINT, formatCivilVerticalsLabel } from '../constants/civilVerticals';
import { ENTITY_DISCIPLINE_OPTIONS, formatEntityDisciplinesLabel } from '../constants/entityDisciplines';
import {
  captureEntityFromFormState,
  createEmptyEntity,
  defaultEntitiesForCategory,
  entityFromFlatProject,
  entityToFlatProjectMirror,
  entityToFormPatch,
  getInfraMetricsProfile,
  loadProjectEntities,
  needsEntityPayloadMigration,
  parseDocumentTagsFromProject,
  serializeEntitiesData,
  stripEntityPayloadFromDescription,
} from '../utils/projectEntityHelpers';
import InfraTypeSelector from './InfraTypeSelector';
import {
  formatInfraTypesLabel,
  getDesignStandardOptions,
  getInfraUnitLabels,
  MATERIAL_CONTEXT_OPTIONS,
} from '../constants/infrastructureElementTypes';
import { formatAppliesToLabel } from '../utils/docAppliesToEntity';


const toYMD = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const y   = d.getUTCFullYear();
  const m   = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getUnitLabels = (occupancyRegion) => getInfraUnitLabels(occupancyRegion);

const SPRINKLER_OPTIONS = [
  { value: 'entire_building',        label: 'Entire Building' },
  { value: 'basement_only',          label: 'Basement Only' },
  { value: 'in_lieu_of_roof_rating', label: 'In Lieu of Roof Rating' },
  { value: 'not_required',           label: 'Not Required' },
  { value: 'unknown',                label: 'Unknown' },
];

const YES_NO_UNKNOWN = [
  { value: 'yes',     label: 'Yes' },
  { value: 'no',      label: 'No' },
  { value: 'unknown', label: 'Unknown' },
];


function ToggleGroup({ value, options, onChange }) {
  return (
    <div className="toggle-group">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`toggle-btn${value === opt.value ? ' active' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}


export default function ProjectSettings({
  project,
  projectId,
  onProjectDeleted,
  onProjectUpdated,
  onTabChange,
  onDirtyChange,
  planKey,
}) {
  const navigate = useNavigate();
  const auth     = useAuth();
  const { showAlert, showConfirm, showPrompt } = useAlertDialog();
  const id       = project?.id ?? projectId;

  const [name,             setName]             = useState(project?.name || '');
  const [desc,             setDesc]             = useState(stripEntityPayloadFromDescription(project?.description || ''));
  const [address,          setAddress]          = useState(project?.location || '');
  const [zoning,           setZoning]           = useState(project?.zoningDesignation || '');
  const [projectType,      setProjectType]      = useState(project?.type || '');
  const [phase,            setPhase]            = useState(project?.phase || '');
  const [status,           setStatus]           = useState(project?.status || '');
  const [start,            setStart]            = useState(project?.startDate || '');
  const [end,              setEnd]              = useState(toYMD(project?.estimatedCompletion) || '');
  const [occupancyRegion,  setOccupancyRegion]  = useState(project?.occupancyRegion || 'CA');
  const [occupancyTypes,   setOccupancyTypes]   = useState(project?.occupancyTypes || []);
  const [occupancyCodes,   setOccupancyCodes]   = useState(project?.occupancyCodes || []);
  const [majorOccupancy,   setMajorOccupancy]   = useState(project?.majorOccupancy || '');
  const [occOpen,          setOccOpen]          = useState(false);
  const [occSearch,        setOccSearch]        = useState('');

  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError,   setTeamError]   = useState(null);

  const [projectMembers, setProjectMembers] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [addOpen,        setAddOpen]        = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole,   setSelectedRole]   = useState('MEMBER');

  const [geo,                           setGeo]                           = useState(project?.geo || null);
  const [buildingFootprintArea,         setBuildingFootprintArea]         = useState(project?.buildingFootprintArea ?? '');
  const [buildingFootprintAreaExisting, setBuildingFootprintAreaExisting] = useState('');
  const [buildingFootprintAreaProposed, setBuildingFootprintAreaProposed] = useState('');
  const [grossFloorAreaExisting,        setGrossFloorAreaExisting]        = useState('');
  const [grossFloorAreaProposed,        setGrossFloorAreaProposed]        = useState('');
  const [siteArea,                setSiteArea]                = useState(project?.siteArea ?? '');
  const [grossFloorArea,          setGrossFloorArea]          = useState(project?.grossFloorArea ?? '');
  const [storeysAboveGrade,       setStoreysAboveGrade]       = useState(project?.storeysAboveGrade ?? '');
  const [storeysBelowGrade,       setStoreysBelowGrade]       = useState(project?.storeysBelowGrade ?? '');
  const [averageGrade,            setAverageGrade]            = useState(project?.averageGrade ?? '');
  const [adjacentStreets,         setAdjacentStreets]         = useState(project?.adjacentStreets ?? '');
  const [roughConstructionBudget, setRoughConstructionBudget] = useState(project?.roughConstructionBudget ?? '');
  const [constructionType,        setConstructionType]        = useState(project?.constructionType || '');
  const [buildingHeight,          setBuildingHeight]          = useState(project?.buildingHeight ?? '');
  const [highBuilding,            setHighBuilding]            = useState(project?.highBuilding ?? false);
  const [sprinklerSystem,         setSprinklerSystem]         = useState(project?.sprinklerSystem || '');
  const [fireAlarm,               setFireAlarm]               = useState(project?.fireAlarm || '');
  const [standpipe,               setStandpipe]               = useState(project?.standpipe || '');
  const [hazardousSubstances,     setHazardousSubstances]     = useState(project?.hazardousSubstances ?? false);
  const [classificationOverride,  setClassificationOverride]  = useState(null);

  const [projectCategory,   setProjectCategory]   = useState(DEFAULT_PROJECT_CATEGORY);
  const [entities,          setEntities]          = useState([]);
  const [activeEntityIdx,   setActiveEntityIdx]   = useState(0);
  const [entityMeta,        setEntityMeta]        = useState({ name: 'Primary Building', structureKind: 'building' });

  const [ctWarningOpen, setCtWarningOpen] = useState(false);
  const [ctConfirmText, setCtConfirmText] = useState('');
  const [pendingCtType, setPendingCtType] = useState('');
  const originalCtRef = useRef(project?.constructionType || '');
  const [pendingReExtraction, setPendingReExtraction] = useState(false);
  const [reFiltering, setReFiltering] = useState(false);
  const [classifiedDocs, setClassifiedDocs] = useState([]);
  const [documentTags, setDocumentTags] = useState({});
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  const [activeSection, setActiveSection] = useState('project-info');

  const OCCUPANCY_LIST    = useMemo(() => occupancyRegion === 'CA' ? OCCUPANCY_TYPES_CA  : OCCUPANCY_TYPES_US,  [occupancyRegion]);
  const CONSTRUCTION_LIST = useMemo(() => occupancyRegion === 'CA' ? CONSTRUCTION_TYPES_CA : CONSTRUCTION_TYPES_US, [occupancyRegion]);

  const occupancyByKey = useMemo(() => {
    const m = new Map();
    for (const [key, title, desc, code] of OCCUPANCY_LIST) m.set(key, { key, title, desc, code });
    return m;
  }, [OCCUPANCY_LIST]);

  const filteredOccupancies = useMemo(() => {
    const q = occSearch.toLowerCase();
    if (!q) return OCCUPANCY_LIST;
    return OCCUPANCY_LIST.filter(([key, title, desc, code]) =>
      title.toLowerCase().includes(q) || desc.toLowerCase().includes(q) ||
      key.toLowerCase().includes(q)   || code.toLowerCase().includes(q)
    );
  }, [occSearch, OCCUPANCY_LIST]);

  const { areaLabel, linearLabel, longLinearLabel, largeAreaLabel } = getUnitLabels(occupancyRegion);
  const isAreaSplit = projectType === 'addition' || projectType === 'change_of_use';
  const categoryProfile = useMemo(
    () => getProjectCategoryProfile(projectCategory),
    [projectCategory]
  );

  const applyFormPatch = (patch) => {
    if (!patch) return;
    setEntityMeta(patch.entityMeta || { name: 'Primary Building', structureKind: 'building' });
    setOccupancyTypes(patch.occupancyTypes || []);
    setOccupancyCodes(patch.occupancyCodes || []);
    setMajorOccupancy(patch.majorOccupancy || '');
    setStoreysAboveGrade(patch.storeysAboveGrade ?? '');
    setStoreysBelowGrade(patch.storeysBelowGrade ?? '');
    setBuildingFootprintArea(patch.buildingFootprintArea ?? '');
    setBuildingFootprintAreaExisting(patch.buildingFootprintAreaExisting ?? '');
    setBuildingFootprintAreaProposed(patch.buildingFootprintAreaProposed ?? '');
    setGrossFloorAreaExisting(patch.grossFloorAreaExisting ?? '');
    setGrossFloorAreaProposed(patch.grossFloorAreaProposed ?? '');
    setGrossFloorArea(patch.grossFloorArea ?? '');
    setAverageGrade(patch.averageGrade ?? '');
    setAdjacentStreets(patch.adjacentStreets ?? '');
    setBuildingHeight(patch.buildingHeight ?? '');
    setConstructionType(patch.constructionType || '');
    setHighBuilding(patch.highBuilding ?? false);
    setSprinklerSystem(patch.sprinklerSystem || '');
    setFireAlarm(patch.fireAlarm || '');
    setStandpipe(patch.standpipe || '');
    setHazardousSubstances(patch.hazardousSubstances ?? false);
    setClassificationOverride(patch.classificationOverride ?? null);
  };

  const getFormCaptureState = () => ({
    entityMeta,
    projectCategory,
    occupancyTypes,
    occupancyCodes,
    majorOccupancy,
    classificationOverride,
    occupancyRegion,
    storeysAboveGrade,
    buildingFootprintArea,
    grossFloorArea,
    storeysBelowGrade,
    averageGrade,
    adjacentStreets,
    buildingHeight,
    constructionType,
    highBuilding,
    sprinklerSystem,
    fireAlarm,
    standpipe,
    hazardousSubstances,
    buildingFootprintAreaExisting,
    buildingFootprintAreaProposed,
    grossFloorAreaExisting,
    grossFloorAreaProposed,
    isAreaSplit,
  });

  const commitActiveEntityToList = (list, idx) => {
    const captured = captureEntityFromFormState(getFormCaptureState());
    const next = [...(list || [])];
    const prev = next[idx] || {};
    next[idx] = {
      ...prev,
      ...captured,
      id: prev.id || captured.id,
      name: entityMeta?.name || prev.name || captured.name,
      structureKind: entityMeta?.structureKind || prev.structureKind || captured.structureKind,
      infraType: entityMeta?.infraType ?? prev.infraType ?? '',
      infraTypes: entityMeta?.infraTypes ?? prev.infraTypes ?? [],
      infraTypeCodes: entityMeta?.infraTypeCodes ?? prev.infraTypeCodes ?? [],
      primaryInfraType: entityMeta?.primaryInfraType ?? prev.primaryInfraType ?? '',
      infraFamily: entityMeta?.infraFamily ?? prev.infraFamily ?? '',
      scope: entityMeta?.scope ?? prev.scope ?? '',
      designStandard: entityMeta?.designStandard ?? prev.designStandard ?? '',
      linearExtent: entityMeta?.linearExtent ?? prev.linearExtent ?? '',
      area: entityMeta?.area ?? prev.area ?? '',
      rowWidth: entityMeta?.rowWidth ?? prev.rowWidth ?? '',
      networkDiameter: entityMeta?.networkDiameter ?? prev.networkDiameter ?? '',
      burialDepth: entityMeta?.burialDepth ?? prev.burialDepth ?? '',
      materialContext: entityMeta?.materialContext ?? prev.materialContext ?? '',
      environmentalFloodplain: entityMeta?.environmentalFloodplain ?? prev.environmentalFloodplain ?? 'unknown',
      environmentalSwm: entityMeta?.environmentalSwm ?? prev.environmentalSwm ?? 'unknown',
      civilVerticals: entityMeta?.civilVerticals ?? prev.civilVerticals ?? [],
      scopeOfWork: entityMeta?.scopeOfWork ?? prev.scopeOfWork ?? '',
      disciplines: entityMeta?.disciplines || prev.disciplines || [],
    };
    return next;
  };

  const switchEntityTab = (idx) => {
    if (idx === activeEntityIdx || idx < 0 || idx >= entities.length) return;
    const updated = commitActiveEntityToList(entities, activeEntityIdx);
    setEntities(updated);
    setActiveEntityIdx(idx);
    applyFormPatch(entityToFormPatch(updated[idx]));
    setIsDirty(true);
  };

  const handleCategoryChange = async (nextCat) => {
    if (!nextCat || nextCat === projectCategory) return;
    const confirmed = await showConfirm({
      type: 'warning',
      title: 'Change project category?',
      message: 'Changing category may hide or reset entity-specific fields. Continue?',
      confirmLabel: 'Change category',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    const defaults = defaultEntitiesForCategory(nextCat);
    setProjectCategory(nextCat);
    setEntities(defaults);
    setActiveEntityIdx(0);
    applyFormPatch(entityToFormPatch(defaults[0]));
    setIsDirty(true);
  };

  const handleAddEntity = () => {
    const profile = getProjectCategoryProfile(projectCategory);
    const updated = commitActiveEntityToList(entities, activeEntityIdx);
    const n = updated.length + 1;
    const label = profile.entityNameLabel?.replace(/ Name$/i, '') || 'Entity';
    updated.push(createEmptyEntity({ name: `${label} ${n}`, sortOrder: n - 1 }, profile));
    setEntities(updated);
    switchEntityTab(updated.length - 1);
  };

  const handleRemoveEntity = async () => {
    if (entities.length <= 1) return;
    const confirmed = await showConfirm({
      type: 'warning',
      title: 'Remove this entity?',
      message: 'Data for this tab will be removed from the project setup.',
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    const updated = commitActiveEntityToList(entities, activeEntityIdx);
    updated.splice(activeEntityIdx, 1);
    const nextIdx = Math.min(activeEntityIdx, updated.length - 1);
    setEntities(updated);
    setActiveEntityIdx(nextIdx);
    applyFormPatch(entityToFormPatch(updated[nextIdx]));
    setIsDirty(true);
  };

  const buildingClassification = occupancyRegion === 'CA'
    ? (isPart9Canada({ storeysAboveGrade, buildingFootprintArea, occupancyCodes })
        ? 'Part 9 — Small Building (NBC/OBC)'
        : 'Part 3 — Large Building (NBC/OBC)')
    : 'IBC (US)';

  const populate = (src) => {
    setName(src?.name || '');
    setDesc(stripEntityPayloadFromDescription(src?.description || ''));
    setDocumentTags(parseDocumentTagsFromProject(src));
    setAddress(src?.location || '');
    setZoning(src?.zoningDesignation || '');
    setProjectType(src?.type || '');
    setPhase(src?.phase || '');
    setStatus(src?.status || '');
    setStart(toYMD(src?.startDate) || '');
    setEnd(toYMD(src?.estimatedCompletion) || '');
    setOccupancyRegion(src?.occupancyRegion || 'CA');
    setOccupancyTypes(src?.occupancyTypes || []);
    setOccupancyCodes(src?.occupancyCodes || []);
    setMajorOccupancy(src?.majorOccupancy || '');
    setBuildingFootprintArea(src?.buildingFootprintArea ?? '');

    const srcIsSplit = src?.type === 'addition' || src?.type === 'change_of_use';
    const hasFpSplit = src?.buildingFootprintAreaExisting != null || src?.buildingFootprintAreaProposed != null;
    const hasGfaSplit = src?.grossFloorAreaExisting != null || src?.grossFloorAreaProposed != null;

    setBuildingFootprintAreaExisting(
      srcIsSplit && !hasFpSplit && src?.buildingFootprintArea != null
        ? String(src.buildingFootprintArea)
        : (src?.buildingFootprintAreaExisting ?? '')
    );
    setBuildingFootprintAreaProposed(src?.buildingFootprintAreaProposed ?? '');
    setGrossFloorAreaExisting(
      srcIsSplit && !hasGfaSplit && src?.grossFloorArea != null
        ? String(src.grossFloorArea)
        : (src?.grossFloorAreaExisting ?? '')
    );
    setGrossFloorAreaProposed(src?.grossFloorAreaProposed ?? '');
    setSiteArea(src?.siteArea ?? '');
    setGrossFloorArea(src?.grossFloorArea ?? '');
    setStoreysAboveGrade(src?.storeysAboveGrade ?? '');
    setStoreysBelowGrade(src?.storeysBelowGrade ?? '');
    setAverageGrade(src?.averageGrade ?? '');
    setAdjacentStreets(src?.adjacentStreets ?? '');
    setRoughConstructionBudget(src?.roughConstructionBudget ?? '');
    setConstructionType(src?.constructionType || '');
    originalCtRef.current = src?.constructionType || '';
    setBuildingHeight(src?.buildingHeight ?? '');
    setHighBuilding(src?.highBuilding ?? false);
    setSprinklerSystem(src?.sprinklerSystem || '');
    setFireAlarm(src?.fireAlarm || '');
    setStandpipe(src?.standpipe || '');
    setHazardousSubstances(src?.hazardousSubstances ?? false);
    const autoVal = (() => {
      const region = src?.occupancyRegion || 'CA';
      if (region !== 'CA') return 'IBC (US)';
      return isPart9Canada({
        storeysAboveGrade: src?.storeysAboveGrade,
        buildingFootprintArea: src?.buildingFootprintArea,
        occupancyCodes: src?.occupancyCodes,
      }) ? 'Part 9' : 'Part 3';
    })();
    setClassificationOverride(
      src?.buildingClassification && src.buildingClassification !== autoVal
        ? src.buildingClassification
        : null
    );

    let { projectCategory: cat, entities: ents } = loadProjectEntities(src);
    console.log('populate →', { cat, entCount: ents?.length, srcCat: src?.projectCategory, hasEntitiesData: !!src?.entitiesData });
    setProjectCategory(cat);
    setEntities(ents.length ? ents : [entityFromFlatProject(src, getProjectCategoryProfile(cat))]);
    setActiveEntityIdx(0);
    applyFormPatch(entityToFormPatch(ents[0] || entityFromFlatProject(src, getProjectCategoryProfile(cat))));

    setIsDirty(false);
  };

  useEffect(() => { if (project) populate(project); }, [project]);

  useEffect(() => {
    if (!project?.id) return;
    let alive = true;
    (async () => {
      if (!needsEntityPayloadMigration(project)) return;
      const migrated = await ProjectService.migrateEntityPayloadIfNeeded(project);
      if (alive && migrated) populate(migrated);
    })();
    return () => { alive = false; };
  }, [project?.id]);

  useEffect(() => {
    if (!id || project) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true); setError(null);
        const fetched = await ProjectService.getProject(id);
        if (alive) populate(fetched);
      } catch (e) { if (alive) setError(e); }
      finally     { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id, project]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      try {
        setTeamLoading(true); setTeamError(null);
        const proj      = project ? project : await ProjectService.getProject(id);
        const companyId = proj?.companyId;
        if (!companyId) throw new Error('Project is missing companyId.');
        const pm = []; let pmNext = null;
        do {
          const res = await graphqlRequest({ query: projectMembersByProjectLite, variables: { projectId: id, limit: 200, nextToken: pmNext } });
          const conn = res?.data?.projectMembersByProject;
          pm.push(...(conn?.items || [])); pmNext = conn?.nextToken || null;
        } while (pmNext);
        const cm = []; let cmNext = null;
        do {
          const res = await graphqlRequest({ query: companyMembersByCompany, variables: { companyId, limit: 200, nextToken: cmNext } });
          const conn = res?.data?.companyMembersByCompany;
          cm.push(...(conn?.items || [])); cmNext = conn?.nextToken || null;
        } while (cmNext);
        if (!alive) return;
        setProjectMembers(pm);
        setCompanyMembers(cm.filter(m => m?.status === 'ACTIVE'));
      } catch (e) { if (!alive) return; setTeamError(e); }
      finally     { if (alive) setTeamLoading(false); }
    })();
    return () => { alive = false; };
  }, [id, project]);

  useEffect(() => {
    if (!id || entities.length <= 1) {
      setClassifiedDocs([]);
      return;
    }
    let alive = true;
    (async () => {
      try {
        setDocsLoading(true);
        setDocsError(null);
        const items = [];
        let nextToken = null;
        do {
          const res = await graphqlRequest({
            query: classifiedDocumentsByProjectWithTags,
            variables: { projectId: id, limit: 100, nextToken },
          });
          const conn = res?.data?.classifiedDocumentsByProject;
          items.push(...(conn?.items || []));
          nextToken = conn?.nextToken || null;
        } while (nextToken);
        if (alive) setClassifiedDocs(items.filter(Boolean));
      } catch (e) {
        if (alive) setDocsError(e);
      } finally {
        if (alive) setDocsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, entities.length]);

  const handleDocTagChange = async (docId, entityIds) => {
    if (!docId) return;
    const ids = Array.isArray(entityIds) ? entityIds.filter(Boolean) : [];
    const nextTags = { ...documentTags };
    if (ids.length) nextTags[docId] = ids;
    else delete nextTags[docId];
    setDocumentTags(nextTags);
    setClassifiedDocs((prev) => prev.map((d) => (
      d.id === docId ? { ...d, projectBuildingDataIds: ids } : d
    )));

    try {
      await graphqlRequest({
        query: updateClassifiedDocumentTags,
        variables: { input: { id: docId, projectBuildingDataIds: ids } },
      });
    } catch (e) {
      await showAlert({ type: 'danger', title: 'Update failed', message: e?.message || 'Could not update document tag.' });
    }
  };

  const toggleDocEntityTag = (docId, entityId, currentIds) => {
    const cur = currentIds || [];
    const next = cur.includes(entityId)
      ? cur.filter((x) => x !== entityId)
      : [...cur, entityId];
    handleDocTagChange(docId, next);
  };

  const toggleEntityDiscipline = (disciplineId) => {
    setEntityMeta((prev) => {
      const cur = prev.disciplines || [];
      const next = cur.includes(disciplineId)
        ? cur.filter((x) => x !== disciplineId)
        : [...cur, disciplineId];
      return { ...prev, disciplines: next };
    });
    setIsDirty(true);
  };

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const getScrollParent = (el) => {
    let node = el?.parentElement;
    while (node && node !== document.body) {
      const oy = window.getComputedStyle(node).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const getStickyOffset = () => {
    const ph = document.querySelector('.ps-page .page-header');
    if (!ph) return 0;
    const pos = window.getComputedStyle(ph).position;
    return (pos === 'sticky' || pos === 'fixed') ? ph.offsetHeight + 8 : 8;
  };

  const scrollToSection = (sid) => {
    const el = document.getElementById(sid);
    if (!el) return;
    const scroller = getScrollParent(el);
    const top = el.getBoundingClientRect().top
      - scroller.getBoundingClientRect().top
      + scroller.scrollTop - getStickyOffset();
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  useEffect(() => {
    const first = document.querySelector('.settings-card[id]');
    if (!first) return;
    const scroller = getScrollParent(first);
    const sections = Array.from(document.querySelectorAll('.settings-card[id]'));
    if (!sections.length) return;

    const onScroll = () => {
      const atBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 4;
      if (atBottom) { setActiveSection(sections[sections.length - 1].id); return; }
      const scTop = scroller.getBoundingClientRect().top;
      const threshold = getStickyOffset() + 12;
      let current = sections[0].id;
      for (const s of sections) {
        if (s.getBoundingClientRect().top - scTop <= threshold) current = s.id;
      }
      setActiveSection(current);
    };

    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const myUserId = useMemo(() => {
    const fromAuth = auth?.user?.id || auth?.user?.userId || auth?.user?.sub || auth?.user?.username;
    if (fromAuth) return String(fromAuth);
    try { const t = localStorage.getItem('id_token'); if (!t) return null; const p = jwtDecode(t); return String(p?.sub || p?.username || p?.['cognito:username'] || ''); } catch { return null; }
  }, [auth]);

  const myProjectRole    = useMemo(() => { if (!myUserId) return 'MEMBER'; const mine = projectMembers.find(pm => String(pm?.userId) === String(myUserId)); return mine?.role || 'MEMBER'; }, [myUserId, projectMembers]);
  const canManageTeam    = useMemo(() => myProjectRole === 'OWNER' || myProjectRole === 'ADMIN', [myProjectRole]);
  const canDeleteProject = useMemo(() => myProjectRole === 'OWNER' || myProjectRole === 'ADMIN', [myProjectRole]);

  const projectMemberUserIds  = useMemo(() => new Set(projectMembers.map(m => m?.userId).filter(Boolean)), [projectMembers]);
  const companyMemberByUserId = useMemo(() => { const map = new Map(); for (const cm of companyMembers) { if (cm?.userId) map.set(cm.userId, cm); } return map; }, [companyMembers]);
  const availableCompanyMembers = useMemo(() => companyMembers.filter(cm => cm?.status === 'ACTIVE').filter(cm => !cm?.userId || !projectMemberUserIds.has(cm.userId)), [companyMembers, projectMemberUserIds]);

  const toggleOccType = (key, code) => {
    setIsDirty(true);
    setOccupancyTypes(prev => {
      const isSelected = prev.includes(key);
      const nextTypes  = isSelected ? prev.filter(k => k !== key) : [...prev, key];
      setOccupancyCodes(prevCodes => isSelected ? prevCodes.filter(c => c !== code) : [...prevCodes, code]);
      setMajorOccupancy(prevMajor => {
        if (nextTypes.length === 0) return '';
        if (nextTypes.length === 1) return nextTypes[0];
        if (prevMajor && nextTypes.includes(prevMajor)) return prevMajor;
        return nextTypes[0];
      });
      return nextTypes;
    });
  };

  const handleAddressSelect = async (addressData) => {
    setAddress(addressData.address || '');
    setGeo(addressData.location || null);
    setIsDirty(true);
    const nextRegion = addressData.countryCode === 'CA' ? 'CA' : 'US';
    if (nextRegion === occupancyRegion) return;
    setOccupancyRegion(nextRegion); setOccupancyTypes([]); setOccupancyCodes([]);
    setMajorOccupancy(''); setOccSearch(''); setConstructionType(''); setOccOpen(true);
    setClassificationOverride(null);
    setEntities((prev) => {
      const committed = commitActiveEntityToList(prev, activeEntityIdx);
      return committed.map((ent, idx) => idx === activeEntityIdx ? ent : ({
        ...ent,
        occupancyTypes: [],
        occupancyCodes: [],
        majorOccupancy: '',
        constructionType: '',
        classificationOverride: null,
      }));
    });
    await showAlert({ type: 'warning', title: 'Jurisdiction changed', message: `Country changed to ${nextRegion}. Please re-select occupancy types and construction type for all buildings under the new code standard before saving.` });
  };

  const toISODateTime = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
  };

  const collectMissingRequired = (finalEntities) => {
    const profile = categoryProfile;
    const problems = [];
    const multi = finalEntities.length > 1;
    finalEntities.forEach((ent, idx) => {
      const who = multi ? `${ent.name || `Building ${idx + 1}`}: ` : '';
      if (isFieldVisible(profile, 'occupancy')) {
        if ((ent.occupancyTypes?.length || 0) === 0) problems.push(`${who}at least one occupancy type`);
        else if ((ent.occupancyTypes?.length || 0) > 1 && !ent.majorOccupancy) problems.push(`${who}major occupancy`);
      }
      if (isFieldVisible(profile, 'infraTypes')) {
        if ((ent.infraTypes?.length || 0) === 0) problems.push(`${who}at least one element type`);
        else if ((ent.infraTypes?.length || 0) > 1 && !ent.primaryInfraType) problems.push(`${who}primary element type`);
      }
      if (isFieldVisible(profile, 'storeysAboveGrade')) {
        if (!(ent.storeysAboveGrade ?? '').toString().trim()) problems.push(`${who}storeys above grade`);
        if (!(ent.adjacentStreets ?? '').toString().trim()) problems.push(`${who}adjacent streets`);
        const hasFootprint = (ent.buildingFootprintArea ?? '').toString().trim()
          || (ent.buildingFootprintAreaExisting ?? '').toString().trim()
          || (ent.buildingFootprintAreaProposed ?? '').toString().trim();
        if (!hasFootprint) problems.push(`${who}building footprint area`);
      }
      if (isFieldVisible(profile, 'constructionType') && !ent.constructionType) problems.push(`${who}construction type`);
      if (isFieldVisible(profile, 'materialContext') && profile.wizardComplianceRequired !== false && !ent.materialContext) problems.push(`${who}material context`);
      if (isFieldVisible(profile, 'civilVerticals') && !(ent.civilVerticals?.length)) problems.push(`${who}civil verticals`);
    });
    return problems;
  };

  const save = async () => {
    if (!name?.trim()) { await showAlert({ type: 'warning', title: 'Required field', message: 'Please enter a project name.' }); return; }
    if (!id)           { await showAlert({ type: 'danger',  message: 'Missing project id.' }); return; }

    const finalEntities = commitActiveEntityToList(entities, activeEntityIdx);
    setEntities(finalEntities);

    const missing = collectMissingRequired(finalEntities);
    if (missing.length) {
      await showAlert({
        type: 'warning',
        title: 'Missing required fields',
        message: `Please complete the following before saving:\n\n• ${missing.join('\n• ')}`,
      });
      return;
    }
    const primaryEntity = finalEntities[0];
    const mirror = entityToFlatProjectMirror(primaryEntity, occupancyRegion);

    const part9 = occupancyRegion === 'CA' && isPart9Canada({
      storeysAboveGrade: mirror.storeysAboveGrade ?? storeysAboveGrade,
      buildingFootprintArea: mirror.buildingFootprintArea ?? buildingFootprintArea,
      occupancyCodes: mirror.occupancyCodes || occupancyCodes,
    });

    const payload = {
      name, description: desc, location: address,
      zoningDesignation: zoning.trim() === '' ? null : zoning.trim(),
      buildingClassification: mirror.buildingClassification || classificationOverride || (occupancyRegion === 'CA' ? (part9 ? 'Part 9' : 'Part 3') : 'IBC (US)'),
      type: projectType, phase, status,
      startDate: toISODateTime(start), estimatedCompletion: toISODateTime(end),
      occupancyRegion,
      occupancyTypes: mirror.occupancyTypes,
      occupancyCodes: mirror.occupancyCodes,
      majorOccupancy: mirror.majorOccupancy,
      buildingFootprintArea: mirror.buildingFootprintArea,
      buildingFootprintAreaExisting: mirror.buildingFootprintAreaExisting,
      buildingFootprintAreaProposed: mirror.buildingFootprintAreaProposed,
      grossFloorAreaExisting: mirror.grossFloorAreaExisting,
      grossFloorAreaProposed: mirror.grossFloorAreaProposed,
      siteArea: siteArea !== '' ? parseFloat(siteArea) : null,
      grossFloorArea: mirror.grossFloorArea,
      storeysAboveGrade: mirror.storeysAboveGrade,
      storeysBelowGrade: mirror.storeysBelowGrade,
      averageGrade: mirror.averageGrade,
      adjacentStreets: mirror.adjacentStreets,
      roughConstructionBudget: roughConstructionBudget !== '' ? parseFloat(roughConstructionBudget) : null,
      constructionType: mirror.constructionType,
      buildingHeight: mirror.buildingHeight,
      highBuilding: mirror.highBuilding,
      sprinklerSystem: mirror.sprinklerSystem,
      fireAlarm: mirror.fireAlarm,
      standpipe: mirror.standpipe,
      hazardousSubstances: mirror.hazardousSubstances,
      projectCategory,
      entitiesData: serializeEntitiesData(finalEntities),
    };
    Object.keys(payload).forEach(k => { if (k === 'zoningDesignation') return; if (payload[k] == null || payload[k] === '') delete payload[k]; });

    try {
      await ProjectService.updateProject(id, payload);
      const fresh = await ProjectService.getProject(id);
      onProjectUpdated?.(fresh);

      if (constructionType && constructionType !== originalCtRef.current) {
        phConstructionTypeSet({ projectId: id, method: 'manual' });
      }

      originalCtRef.current = constructionType;
      setIsDirty(false);

      // TODO (backend — Shivani/Dennis): no guard exists against firing this while
      // the initial folder-link extraction (classifyDocumentWorker/extractTextWorker)
      // is still in flight for this project. Construction-type changes now surface
      // earlier in the guided tour, increasing how often users hit this overlap.
      // Needs a project-level extraction-status check before this request fires.
      if (pendingReExtraction) {
        setPendingReExtraction(false);
        try {
          const mirror = entityToFlatProjectMirror(finalEntities[0], occupancyRegion);
          const projectContext = {
            jurisdiction: occupancyRegion,
            majorOccupancyCode: mirror.majorOccupancy,
            zoningDesignation: zoning,
            allOccupancyCodes: mirror.occupancyCodes || [],
            buildingClassification: mirror.buildingClassification,
            projectType,
            storeysAboveGrade: mirror.storeysAboveGrade,
            storeysBelowGrade: mirror.storeysBelowGrade,
            buildingFootprintArea: mirror.buildingFootprintArea,
            adjacentStreets: mirror.adjacentStreets,
            constructionType: mirror.constructionType,
            highBuilding: mirror.highBuilding || false,
            sprinklerSystem: mirror.sprinklerSystem || null,
            hazardousSubstances: mirror.hazardousSubstances || false,
            projectCategory,
            entities: finalEntities,
          };
          const cleanContext = Object.fromEntries(Object.entries(projectContext).filter(([_, v]) => v != null && v !== ''));
          await fetch(`${process.env.REACT_APP_API_BASE_REQUIREMENTS}/projects/${id}/re-extract`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectContext: cleanContext, userId: auth?.user?.sub || '' }),
          });
          // COMPANY-scope building codes: cheap re-filter against the new settings
          // (reuses CompanyRequirements — no re-extraction). Non-fatal if it fails.
          try {
            if (project?.companyId) {
              await ProjectService.reFilterProject(id, {
                companyId: project.companyId,
                projectContext: cleanContext,
                userId: auth?.user?.profile?.sub || auth?.user?.sub || '',
              });
            }
          } catch (rfErr) {
            console.warn('[save] COMPANY re-filter trigger failed (non-fatal):', rfErr);
          }
          await showAlert({ type: 'success', title: 'Saved & re-filtering started', message: 'Project settings saved. Requirements are being re-filtered against your updated settings — this should only take a few seconds.' });
        } catch (err) {
          console.error(err);
          await showAlert({ type: 'success', title: 'Saved', message: 'Project settings saved, but re-filtering could not be triggered. Please contact support.' });
        }
      } else {
        await showAlert({ type: 'success', title: 'Saved', message: 'Project settings saved successfully.' });
      }
    } catch (e) {
      console.error(e);
      await showAlert({ type: 'danger', title: 'Save failed', message: e?.message || 'Failed to save project settings.' });
    }
  };

  // Manual re-filter: re-run COMPANY building-code filtering against the currently
  // SAVED project settings (cheap — no re-extraction). Useful for iterating on
  // construction-type / occupancy without re-linking the source document.
  const reFilterRequirements = async () => {
    if (!id || !project?.companyId) {
      await showAlert({ type: 'warning', title: 'Nothing to re-filter', message: 'This project has no company-scope building-code requirements to re-filter.' });
      return;
    }
    if (isDirty) {
      const ok = await showConfirm({ type: 'warning', title: 'Unsaved changes', message: 'Re-filter uses the last saved settings. Save first, or re-filter against the saved settings now?', confirmText: 'Re-filter saved', cancelText: 'Cancel' });
      if (!ok) return;
    }
    try {
      setReFiltering(true);
      await ProjectService.reFilterProject(id, {
        companyId: project.companyId,
        projectContext: buildProjectContext(project),
        userId: auth?.user?.profile?.sub || auth?.user?.sub || '',
      });
      await showAlert({ type: 'success', title: 'Re-filtering started', message: 'Building-code requirements are being re-filtered against the current project settings. You’ll get a notification when it completes.' });
    } catch (e) {
      console.error('[reFilterRequirements] failed:', e);
      await showAlert({ type: 'danger', title: 'Re-filter failed', message: e?.message || 'Could not start re-filtering.' });
    } finally {
      setReFiltering(false);
    }
  };

  const cancel = async () => {
    if (!isDirty) return;
    const projectName = project?.name || 'this project';
    const ok = await showConfirm({ type: 'warning', title: 'Discard changes?', message: `Cancel changes to "${projectName}"? Unsaved data will be lost.`, confirmText: 'Discard', cancelText: 'Keep editing' });
    if (ok) populate(project);
  };

  const handleDeleteProject = async () => {
    if (!id)               { await showAlert({ type: 'danger', message: 'Missing project id.' }); return; }
    if (!canDeleteProject) { await showAlert({ type: 'danger', message: 'You do not have permission to delete this project.' }); return; }
    const projectName = (name || project?.name || '').trim() || 'this project';
    const ok = await showConfirm({ type: 'danger', title: `Delete "${projectName}"?`, message: 'This will permanently delete the project and all associated data. This cannot be undone.', confirmText: 'Delete', cancelText: 'Cancel' });
    if (!ok) return;
    const typed = await showPrompt({ type: 'danger', title: 'Confirm deletion', message: 'Type the project name to confirm:', placeholder: projectName, confirmText: 'Delete project', cancelText: 'Cancel' });
    if ((typed || '').trim() !== projectName) { await showAlert({ type: 'warning', title: 'Name did not match', message: 'Project name did not match. Delete cancelled.' }); return; }
    try {
      setLoading(true);
      await ProjectService.deleteProject(id);
      ProjectService.deleteProjectCleanup(id);
      await showAlert({ type: 'success', title: 'Deleted', message: 'Project deleted.' });
      onProjectDeleted?.(id);
      navigate('/', { replace: true });
    } catch (e) {
      console.error(e);
      await showAlert({ type: 'danger', title: 'Delete failed', message: e?.message || 'Failed to delete project.' });
    } finally { setLoading(false); }
  };

  const handleRemoveMember = async (member) => {
    if (!canManageTeam) { await showAlert({ type: 'danger', message: 'You do not have permission to manage team members.' }); return; }
    if (!member?.id || member?.role === 'OWNER') return;
    const cm = member?.userId ? companyMemberByUserId.get(member.userId) : null;
    const ok = await showConfirm({ type: 'warning', title: 'Remove member', message: `Remove ${cm?.email || 'this user'} from the project?`, confirmText: 'Remove', cancelText: 'Cancel' });
    if (!ok) return;
    try {
      setTeamLoading(true);
      await graphqlRequest({ query: deleteProjectMember, variables: { input: { id: member.id } } });
      setProjectMembers(prev => prev.filter(m => m.id !== member.id));
    } catch (e) { await showAlert({ type: 'danger', message: e?.message || 'Failed to remove member.' }); }
    finally { setTeamLoading(false); }
  };

  const handleAddMember = async () => {
    if (!canManageTeam)  { await showAlert({ type: 'danger',  message: 'You do not have permission to add team members.' }); return; }
    if (!selectedUserId) { await showAlert({ type: 'warning', message: 'Select a company member first.' }); return; }
    if (!companyMembers.find(cm => cm.userId === selectedUserId)) { await showAlert({ type: 'danger', message: 'Selected member is not valid.' }); return; }
    try {
      setTeamLoading(true);
      const res = await graphqlRequest({ query: createProjectMember, variables: { input: { projectId: id, userId: selectedUserId, role: selectedRole } } });
      if (!res?.data?.createProjectMember) throw new Error('createProjectMember returned no data.');
      const pm = []; let pmNext = null;
      do {
        const r = await graphqlRequest({ query: projectMembersByProjectLite, variables: { projectId: id, limit: 200, nextToken: pmNext } });
        const conn = r?.data?.projectMembersByProject;
        pm.push(...(conn?.items || [])); pmNext = conn?.nextToken || null;
      } while (pmNext);
      setProjectMembers(pm); setAddOpen(false); setSelectedUserId(''); setSelectedRole('MEMBER');
    } catch (e) { await showAlert({ type: 'danger', message: e?.message || 'Failed to add member.' }); }
    finally { setTeamLoading(false); }
  };

  const handleCtChange = async (newType) => {
    const orig = originalCtRef.current;
    if (!newType || !orig || newType === orig) {
      setConstructionType(newType);
      setIsDirty(true);
      return;
    }
    try {
      const res = await graphqlRequest({
        query: classifiedDocumentsByProjectBuildingCode,
        variables: { projectId: id, complianceType: 'BUILDING_CODE', limit: 1 },
      });
      const hasBCDocs = (res?.data?.classifiedDocumentsByProject?.items?.length ?? 0) > 0;
      if (hasBCDocs) {
        setPendingCtType(newType); setCtConfirmText(''); setCtWarningOpen(true);
      } else {
        setConstructionType(newType);
        setIsDirty(true);
      }
    } catch (e) {
      console.error('Failed to check classified documents:', e);
      setPendingCtType(newType); setCtConfirmText(''); setCtWarningOpen(true);
    }
  };

  const handleCtConfirm = async () => {
    const expectedLabel = CONSTRUCTION_LIST.find(([key]) => key === pendingCtType)?.[1] || '';
    if (ctConfirmText.trim() !== expectedLabel) {
      await showAlert({ type: 'warning', title: 'Name did not match', message: 'Construction type name did not match. Please type it exactly.' });
      return;
    }
    setConstructionType(pendingCtType);
    setPendingReExtraction(true);
    setIsDirty(true);
    setCtWarningOpen(false);
    setCtConfirmText('');
    setPendingCtType('');
  };


  if (loading) return <div className="ps-page" style={{ padding: 24, color: '#666' }}>Loading project…</div>;
  if (error)   return <div className="ps-page" style={{ padding: 24, color: '#c62828' }}>Failed to load project.</div>;

  const TOC_ITEMS = [
    { id: 'project-info',       num: '1', label: 'Project Information' },
    { id: 'location',           num: '2', label: 'Location & Compliance' },
    { id: 'occupancy',          num: '3', label: categoryProfile.tocLabel || 'Occupancy & Classification' },
    { id: 'building-metrics',   num: '4', label: 'Building Metrics' },
    { id: 'construction-type',  num: '5', label: 'Construction Type', warn: !constructionType },
    { id: 'compliance-context', num: '6', label: 'Compliance Context' },
    ...(entities.length > 1 ? [{ id: 'project-documents', num: '7', label: 'Document Tags' }] : []),
    ...(planHasTeamAccess(planKey) ? [{ id: 'team', num: entities.length > 1 ? '8' : '7', label: 'Team & Access' }] : []),
  ];

  const showBuildingMetrics = isFieldVisible(categoryProfile, 'storeysAboveGrade')
    || isFieldVisible(categoryProfile, 'buildingFootprintArea')
    || isFieldVisible(categoryProfile, 'grossFloorArea');
  const showInfraMetrics = isFieldVisible(categoryProfile, 'linearExtent')
    || isFieldVisible(categoryProfile, 'area');
  const showOccupancySection = isFieldVisible(categoryProfile, 'occupancy')
    || isFieldVisible(categoryProfile, 'elementName')
    || isFieldVisible(categoryProfile, 'infraTypes')
    || isFieldVisible(categoryProfile, 'civilVerticals');
  const infraMetricsProfile = getInfraMetricsProfile({
    primaryInfraType: entityMeta?.primaryInfraType,
    infraTypes: entityMeta?.infraTypes,
    infraFamily: entityMeta?.infraFamily,
  });
  const infraSummaryLabel = entities.length > 0
    ? `${entities.length} site element${entities.length === 1 ? '' : 's'}`
    : '';
  const primaryInfraSummary = entityMeta?.infraTypes?.length
    ? formatInfraTypesLabel(entityMeta.infraTypes.slice(0, 2), occupancyRegion)
    : '';

  return (
    <div className="ps-page">

      <header className="page-header" style={{ flexDirection: 'column', height: 'auto', padding: 0, alignItems: 'stretch' }}>
        {isDirty && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',   // right-justify the notice within the content column
            gap: '0.4rem',
            height: 'var(--header-h)',
            // match the content column (.page-body): max-width + horizontal padding
            maxWidth: '1080px',
            width: '100%',
            margin: '0 auto',
            padding: '0 2rem',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>
              You have unsaved changes — click <strong style={{ margin: '0 3px' }}>Save Settings</strong> to apply them.
              {pendingReExtraction && <span style={{ marginLeft: '0.5rem' }}>Re-filtering will trigger on save.</span>}
            </span>
          </div>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',   // heading left, actions right
          // match the content column (.page-body): max-width + horizontal padding
          maxWidth: '1080px',
          width: '100%',
          margin: '0 auto',
          padding: '1.25rem 2rem 0.85rem',   // extra top/bottom margin above heading + buttons
        }}>
          <h1 style={{ margin: 0 }}>Project Settings</h1>
          <div className="header-actions">
            {process.env.REACT_APP_DEV_MODE === 'true' && project?.companyId && (
              <button
                className="btn btn-secondary"
                onClick={reFilterRequirements}
                disabled={reFiltering}
                title="Dev-only: re-run building-code requirement filtering against the current project settings (no re-extraction)"
              >
                {reFiltering ? 'Re-filtering…' : 'Re-filter requirements'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={cancel}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={loading}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
              </svg>
              Save Settings
            </button>
          </div>
        </div>
      </header>

      {/* ── Page Body ── */}
      <div className="page-body">

        {/* TOC Sidebar */}
        <nav className="toc-sidebar" aria-label="Page sections">
          <div className="toc-label">On this page</div>
          <div className="toc-nav">
            {TOC_ITEMS.map(({ id: sid, num, label, warn }) => (
              <a key={sid} className={`toc-link${activeSection === sid ? ' active' : ''}`} href={`#${sid}`}
                onClick={e => { e.preventDefault(); scrollToSection(sid); }}>
                <span className="toc-num">{num}</span>
                {label}
                {warn && <span className="toc-warn-dot" />}
              </a>
            ))}
          </div>
          <div className="research-callout">
            <div className="research-callout-header">
              <svg className="research-callout-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <span className="research-callout-title">Not sure what applies?</span>
            </div>
            <p className="research-callout-body">Research your linked project documents to help fill in these fields.</p>
            <button className="research-callout-link" onClick={() => onTabChange?.('Chat')}>
              Open Research
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </div>
        </nav>

        {/* Cards Column */}
        <div className="cards-column">

          {/* 1. Project Information */}
          <section id="project-info" className="settings-card">
            <div className="card-header">
              <div className="card-title">Project Information</div>
              <div className="card-desc">Basic details about your construction project</div>
            </div>
            <div className="form-group">
              <label className="field-label">Project Name <span className="required-star">*</span></label>
              <input type="text" value={name} onChange={e => { setName(e.target.value); setIsDirty(true); }} placeholder="Enter project name" />
            </div>
            <div className="form-group">
              <label className="field-label">Description <span className="optional">(optional)</span></label>
              <textarea value={desc} onChange={e => { setDesc(e.target.value); setIsDirty(true); }} placeholder="Brief description…" />
            </div>
            <div className="form-group">
              <label className="field-label">Project Category</label>
              <StandardDropdown
                value={projectCategory}
                placeholder="Select category"
                options={PROJECT_CATEGORY_OPTIONS}
                onChange={v => { if (v) handleCategoryChange(v); }}
              />
              <div className="field-hint">Controls how buildings or site elements are organized on this project.</div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">Project Type <span className="required-star">*</span></label>
                <StandardDropdown value={projectType} placeholder="Select type"
                  options={PROJECT_TYPE_OPTIONS}
                  onChange={v => {
                    const wasSplit = projectType === 'addition' || projectType === 'change_of_use';
                    const nowSplit = v === 'addition' || v === 'change_of_use';
                    if (wasSplit && !nowSplit) {
                      const fp = Math.round(((parseFloat(buildingFootprintAreaExisting||'')||0) + (parseFloat(buildingFootprintAreaProposed||'')||0)) * 100) / 100;
                      const gfa = Math.round(((parseFloat(grossFloorAreaExisting||'')||0) + (parseFloat(grossFloorAreaProposed||'')||0)) * 100) / 100;
                      if (fp > 0) setBuildingFootprintArea(String(fp));
                      if (gfa > 0) setGrossFloorArea(String(gfa));
                      setBuildingFootprintAreaExisting('');
                      setBuildingFootprintAreaProposed('');
                      setGrossFloorAreaExisting('');
                      setGrossFloorAreaProposed('');
                    }
                    setProjectType(v || '');
                    setIsDirty(true);
                  }} />
              </div>
              <div className="form-group">
                <label className="field-label">Project Status</label>
                <StandardDropdown value={status} placeholder="Select status"
                  options={PROJECT_STATUS_OPTIONS}
                  onChange={v => { setStatus(v || ''); setIsDirty(true); }} />
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Project Phase</label>
              <StandardDropdown value={phase} placeholder="Select phase"
                options={PROJECT_PHASE_OPTIONS}
                onChange={v => { setPhase(v || ''); setIsDirty(true); }} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">Start Date</label>
                <DatePicker value={start} onChange={v => { setStart(v); setIsDirty(true); }} placeholder="Select date" />
              </div>
              <div className="form-group">
                <label className="field-label">Target Completion</label>
                <DatePicker value={end} onChange={v => { setEnd(v); setIsDirty(true); }} placeholder="Select date" />
              </div>
            </div>
          </section>

          {/* 2. Location & Compliance */}
          <section id="location" className="settings-card">
            <div className="card-header">
              <div className="card-title">Location &amp; Compliance</div>
              <div className="card-desc">Project address determines jurisdiction, unit system, and applicable code lists. Changing country resets occupancy types and construction type.</div>
            </div>
            <div className="form-group">
              <label className="field-label">Project Address <span className="required-star">*</span></label>
              <AddressAutocomplete value={address} onChange={addr => { setAddress(addr); setIsDirty(true); }} onAddressSelect={handleAddressSelect} placeholder="Enter complete project address" />
              {address && (
                <div className="field-hint">
                  Jurisdiction detected: <strong style={{ color: '#333' }}>{occupancyRegion === 'CA' ? 'Canada (CA)' : 'United States (US)'}</strong>
                  {' · '}{areaLabel === 'm²' ? 'Metric units · Federal / Provincial code references' : 'Imperial units · IBC'}
                </div>
              )}
              {isCivilCategory(projectCategory) && (entityMeta?.civilVerticals?.length || entityMeta?.scopeOfWork) && (
                <div className="field-hint" style={{ marginTop: '0.35rem' }}>
                  <strong>{formatCivilVerticalsLabel(entityMeta?.civilVerticals || [])}</strong>
                  {infraSummaryLabel ? ` · ${infraSummaryLabel}` : ''}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="field-label">Zoning Designation <span className="optional">(optional)</span></label>
              <input type="text" value={zoning} onChange={e => { setZoning(e.target.value); setIsDirty(true); }} placeholder="e.g. CR 3.0, R2, MU-1" />
            </div>
          </section>

          {/* 3. Entity profile — occupancy / site elements */}
          {showOccupancySection && (
          <section id="occupancy" className="settings-card">
            <div className="card-header">
              <div className="card-title">{categoryProfile.title}</div>
              <div className="card-desc">{categoryProfile.description}</div>
            </div>

            {categoryProfile.showTabs && entities.length > 0 && (
              <div className="entity-tabs-bar">
                {entities.map((ent, idx) => (
                  <button
                    key={ent.id || idx}
                    type="button"
                    className={`entity-tab${activeEntityIdx === idx ? ' active' : ''}`}
                    onClick={() => switchEntityTab(idx)}
                  >
                    {ent.name || `${categoryProfile.entityNameLabel} ${idx + 1}`}
                  </button>
                ))}
                {categoryProfile.showAddButton && (
                  <button type="button" className="entity-tab entity-tab-add" onClick={handleAddEntity}>
                    + Add
                  </button>
                )}
              </div>
            )}

            {isFieldVisible(categoryProfile, 'civilVerticals') && (
              <>
                <div className="form-group">
                  <label className="field-label">Civil Verticals <span className="required-star">*</span></label>
                  <div className="field-hint" style={{ marginBottom: '0.5rem' }}>{CIVIL_VERTICALS_SELECTION_HINT}</div>
                  <div className="civil-vertical-grid">
                    {CIVIL_VERTICAL_OPTIONS.map((opt) => {
                      const selected = (entityMeta?.civilVerticals || []).includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`civil-vertical-chip${selected ? ' selected' : ''}`}
                          onClick={() => {
                            setEntityMeta((prev) => {
                              const cur = prev.civilVerticals || [];
                              const next = cur.includes(opt.value)
                                ? cur.filter((k) => k !== opt.value)
                                : [...cur, opt.value];
                              return { ...prev, civilVerticals: next };
                            });
                            setIsDirty(true);
                          }}
                        >
                          <span className="civil-vertical-chip-title">{opt.label}</span>
                          <span className="civil-vertical-chip-desc">{opt.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="form-group">
                  <label className="field-label">Scope of Work / Design Brief</label>
                  <textarea
                    rows={4}
                    value={entityMeta?.scopeOfWork || ''}
                    onChange={e => { setEntityMeta(prev => ({ ...prev, scopeOfWork: e.target.value })); setIsDirty(true); }}
                    placeholder="Scope notes or design brief summary"
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
              </>
            )}

            {isFieldVisible(categoryProfile, 'elementName') && (
              <div className="form-group">
                <label className="field-label">{categoryProfile.entityNameLabel} <span className="required-star">*</span></label>
                <input
                  type="text"
                  value={entityMeta?.name || ''}
                  onChange={e => { setEntityMeta(prev => ({ ...prev, name: e.target.value })); setIsDirty(true); }}
                  placeholder={`Enter ${categoryProfile.entityNameLabel?.toLowerCase() || 'name'}`}
                />
              </div>
            )}

            {isFieldVisible(categoryProfile, 'infraTypes') && (
              <>
                <InfraTypeSelector
                  occupancyRegion={occupancyRegion}
                  selectedKeys={entityMeta?.infraTypes || []}
                  primaryKey={entityMeta?.primaryInfraType || ''}
                  onChange={(patch) => { setEntityMeta((prev) => ({ ...prev, ...patch })); setIsDirty(true); }}
                  required
                />
                <div className="form-group">
                  <label className="field-label">Scope</label>
                  <textarea
                    rows={3}
                    value={entityMeta?.scope || ''}
                    onChange={e => { setEntityMeta(prev => ({ ...prev, scope: e.target.value })); setIsDirty(true); }}
                    placeholder="Brief scope description"
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
                {isFieldVisible(categoryProfile, 'designStandard') && (
                  <div className="form-group">
                    <label className="field-label">Design Standard</label>
                    <StandardDropdown
                      value={entityMeta?.designStandard || ''}
                      placeholder="Select design standard (optional)"
                      options={getDesignStandardOptions(occupancyRegion)}
                      onChange={v => { setEntityMeta(prev => ({ ...prev, designStandard: v || '' })); setIsDirty(true); }}
                    />
                  </div>
                )}
              </>
            )}

            {categoryProfile.showPanelActions && entities.length > 1 && (
              <div className="entity-panel-actions">
                <button type="button" className="entity-remove-btn" onClick={handleRemoveEntity}>
                  Remove this {categoryProfile.entityNameLabel?.replace(/ Name$/i, '') || 'entity'}
                </button>
              </div>
            )}

            {isFieldVisible(categoryProfile, 'occupancy') && (
            <>
            <div className="form-group">
              <label className="field-label">Occupancy Types <span className="required-star">*</span> <span className="optional">(select all that apply)</span></label>
              <div className="occ-wrapper">
                <div className="occ-display" onClick={() => setOccOpen(v => !v)}>
                  {occupancyTypes.length === 0
                    ? <span className="occ-placeholder">Click to select occupancy types…</span>
                    : <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', flex: 1 }}>
                        {occupancyTypes.map(key => (
                          <span key={key} className="occ-tag">
                            {occupancyByKey.get(key)?.title || key}
                            <span className="x" onClick={e => { e.stopPropagation(); const item = occupancyByKey.get(key); if (item) toggleOccType(item.key, item.code); }}>×</span>
                          </span>
                        ))}
                      </div>
                  }
                </div>
                <div className={`occ-popup${occOpen ? ' show' : ''}`}>
                  <div className="occ-popup-header">
                    <div className="occ-popup-title">Select Occupancy Types</div>
                    <div className="occ-popup-subtitle">Showing: {occupancyRegion === 'CA' ? 'Canada (Federal/Provincial)' : 'IBC (US / default)'} · Select all that apply</div>
                  </div>
                  <div className="occ-search-wrap">
                    <input type="text" placeholder="Search occupancy types…" value={occSearch} onChange={e => setOccSearch(e.target.value)} />
                  </div>
                  <div className="occ-list">
                    {filteredOccupancies.map(([key, title, desc, code]) => {
                      const checked = occupancyTypes.includes(key);
                      return (
                        <div key={key} className={`occ-option${checked ? ' selected' : ''}`} onClick={() => toggleOccType(key, code)}>
                          <div className={`occ-checkbox${checked ? ' checked' : ''}`}>{checked ? '✓' : ''}</div>
                          <div>
                            <div className="occ-option-title">{title} <span className="occ-option-code">({code.toUpperCase()})</span></div>
                            <div className="occ-option-desc">{desc}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="occ-popup-footer">
                    <span className="occ-count">{occupancyTypes.length} selected</span>
                    <button className="occ-done-btn" onClick={() => setOccOpen(false)}>Done</button>
                  </div>
                </div>
              </div>
              <div className="field-hint">Showing: {occupancyRegion === 'CA' ? 'Canada (Federal/Provincial)' : 'IBC (US)'}. Click to open selector.</div>
            </div>
            <div className="form-group">
              <label className="field-label">Major Occupancy <span className="required-star">*</span></label>
              <StandardDropdown value={majorOccupancy} placeholder={occupancyTypes.length <= 1 ? 'Auto-selected by CODiii' : 'Select Major Occupancy'}
                options={occupancyTypes.map(key => ({ value: key, label: occupancyByKey.get(key)?.title || key }))}
                onChange={v => { setMajorOccupancy(v || ''); setIsDirty(true); }} disabled={occupancyTypes.length <= 1} />
              <div className="field-hint">{occupancyTypes.length <= 1 ? 'Auto-selected — only one occupancy type selected' : 'Select the dominant occupancy type for code classification'}</div>
            </div>
            <div className="form-group">
              <label className="field-label">Building Classification</label>
              {classificationOverride ? (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <StandardDropdown
                      value={classificationOverride}
                      options={
                        occupancyRegion === 'CA'
                          ? [
                              { value: 'Part 9', label: 'Part 9 — Small Building (NBC/OBC)' },
                              { value: 'Part 3', label: 'Part 3 — Large Building (NBC/OBC)' },
                            ]
                          : [
                              { value: 'IBC (US)', label: 'IBC (US)' },
                            ]
                      }
                      onChange={v => { setClassificationOverride(v || null); setIsDirty(true); }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
                    onClick={() => { setClassificationOverride(null); setIsDirty(true); }}
                  >
                    Clear override
                  </button>
                </div>
              ) : (
                <div className="field-readonly" style={{ justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    {buildingClassification}
                    <span className="auto-badge">Auto-computed</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
                    onClick={() => {
                      setClassificationOverride(occupancyRegion === 'CA' ? (isPart9Canada({ storeysAboveGrade, buildingFootprintArea, occupancyCodes }) ? 'Part 9' : 'Part 3') : 'IBC (US)');
                      setIsDirty(true);
                    }}
                  >
                    Override
                  </button>
                </div>
              )}
              <div className="field-hint">
                {classificationOverride
                  ? 'Manual override active — auto-computation is bypassed.'
                  : 'Based on storeys above grade ≤ 3, footprint area ≤ 600 m², and occupancy type'}
              </div>
            </div>
            </>
            )}
          </section>
          )}

          {showInfraMetrics && (
          <section id="infra-metrics" className="settings-card infra-metrics-section">
            <div className="card-header">
              <div className="card-title">Element Metrics</div>
              <div className="card-desc">Optional design parameters for the active civil element — add per element as your project develops. Units: {areaLabel === 'm²' ? 'metric (m, m², km, ha)' : 'imperial (ft, ft², mi, acres)'} based on address.</div>
            </div>
            {infraMetricsProfile.showLinearExtent && (
              <div className="form-group">
                <label className="field-label">
                  Linear Extent ({infraMetricsProfile.family === 'linear' ? longLinearLabel : linearLabel})
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={entityMeta?.linearExtent ?? ''}
                  onChange={e => { if (onlyDecimal(e.target.value)) { setEntityMeta(prev => ({ ...prev, linearExtent: e.target.value })); setIsDirty(true); } }}
                  placeholder={infraMetricsProfile.family === 'linear' ? 'e.g. 0.5' : 'e.g. 120'}
                />
              </div>
            )}
            {infraMetricsProfile.showRowWidth && (
              <div className="form-group">
                <label className="field-label">ROW Width ({linearLabel})</label>
                <input type="number" step="0.01" value={entityMeta?.rowWidth ?? ''} onChange={e => { if (onlyDecimal(e.target.value)) { setEntityMeta(prev => ({ ...prev, rowWidth: e.target.value })); setIsDirty(true); } }} placeholder="e.g. 12" />
              </div>
            )}
            {infraMetricsProfile.showArea && (
              <div className="form-group">
                <label className="field-label">Area ({areaLabel})</label>
                <input type="number" step="0.01" value={entityMeta?.area ?? ''} onChange={e => { if (onlyDecimal(e.target.value)) { setEntityMeta(prev => ({ ...prev, area: e.target.value })); setIsDirty(true); } }} placeholder="e.g. 2500" />
                <div className="field-hint">Large sites may also be expressed in {largeAreaLabel}.</div>
              </div>
            )}
            {infraMetricsProfile.showNetworkDiameter && (
              <div className="form-row">
                <div className="form-group">
                  <label className="field-label">Pipe Diameter ({linearLabel})</label>
                  <input type="number" step="0.01" value={entityMeta?.networkDiameter ?? ''} onChange={e => { if (onlyDecimal(e.target.value)) { setEntityMeta(prev => ({ ...prev, networkDiameter: e.target.value })); setIsDirty(true); } }} />
                </div>
                <div className="form-group">
                  <label className="field-label">Burial Depth ({linearLabel})</label>
                  <input type="number" step="0.01" value={entityMeta?.burialDepth ?? ''} onChange={e => { if (onlyDecimal(e.target.value)) { setEntityMeta(prev => ({ ...prev, burialDepth: e.target.value })); setIsDirty(true); } }} />
                </div>
              </div>
            )}
          </section>
          )}

          {/* 4. Building Metrics */}
          {showBuildingMetrics && (
          <section id="building-metrics" className="settings-card">
            <div className="card-header">
              <div className="card-title">Building Metrics</div>
              <div className="card-desc">Areas, storeys, grade, and height — {areaLabel === 'm²' ? 'metric units (m², m)' : 'imperial units (ft², ft)'} based on address. Area fields split into Existing + Proposed for Addition and Change of Use project types.</div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">Storeys Above Grade <span className="required-star">*</span></label>
                <input type="number" value={storeysAboveGrade} min="1" placeholder="e.g. 3" onChange={e => { if (onlyDigits(e.target.value)) { setStoreysAboveGrade(e.target.value); setIsDirty(true); } }} />
              </div>
              <div className="form-group">
                <label className="field-label">Storeys Below Grade</label>
                <input type="number" value={storeysBelowGrade} min="0" placeholder="e.g. 1" onChange={e => { if (onlyDigits(e.target.value)) { setStoreysBelowGrade(e.target.value); setIsDirty(true); } }} />
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Building Footprint Area ({areaLabel}) <span className="required-star">*</span></label>
              {isAreaSplit ? (
                <>
                  <div className="area-split">
                    <div><div className="sub-label">Existing</div><input type="number" placeholder="0" value={buildingFootprintAreaExisting} onChange={e => { if (onlyDecimal(e.target.value)) { setBuildingFootprintAreaExisting(e.target.value); setIsDirty(true); } }} /></div>
                    <div><div className="sub-label">Proposed</div><input type="number" placeholder="0" value={buildingFootprintAreaProposed} onChange={e => { if (onlyDecimal(e.target.value)) { setBuildingFootprintAreaProposed(e.target.value); setIsDirty(true); } }} /></div>
                  </div>
                  {(buildingFootprintAreaExisting || buildingFootprintAreaProposed || buildingFootprintArea) && (
                    <div className="area-total-chip">Total: <strong>{formatWithCommas(String(Math.round(((parseFloat(buildingFootprintAreaExisting||'')||0)+(parseFloat(buildingFootprintAreaProposed||'')||0) || parseFloat(buildingFootprintArea||'')||0)*100)/100))} {areaLabel}</strong></div>
                  )}
                </>
              ) : (
                <input type="number" value={buildingFootprintArea} placeholder="e.g. 600" onChange={e => { if (onlyDecimal(e.target.value)) { setBuildingFootprintArea(e.target.value); setIsDirty(true); } }} />
              )}
            </div>
            <div className="form-group">
              <label className="field-label">Gross Floor Area ({areaLabel})</label>
              {isAreaSplit ? (
                <>
                  <div className="area-split">
                    <div><div className="sub-label">Existing</div><input type="number" placeholder="0" value={grossFloorAreaExisting} onChange={e => { if (onlyDecimal(e.target.value)) { setGrossFloorAreaExisting(e.target.value); setIsDirty(true); } }} /></div>
                    <div><div className="sub-label">Proposed</div><input type="number" placeholder="0" value={grossFloorAreaProposed} onChange={e => { if (onlyDecimal(e.target.value)) { setGrossFloorAreaProposed(e.target.value); setIsDirty(true); } }} /></div>
                  </div>
                  {(grossFloorAreaExisting || grossFloorAreaProposed || grossFloorArea) && (
                    <div className="area-total-chip">Total: <strong>{formatWithCommas(String(Math.round(((parseFloat(grossFloorAreaExisting||'')||0)+(parseFloat(grossFloorAreaProposed||'')||0) || parseFloat(grossFloorArea||'')||0)*100)/100))} {areaLabel}</strong></div>
                  )}
                </>
              ) : (
                <input type="number" value={grossFloorArea} placeholder="e.g. 1800" onChange={e => { if (onlyDecimal(e.target.value)) { setGrossFloorArea(e.target.value); setIsDirty(true); } }} />
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">Site Area ({areaLabel})</label>
                <input type="number" value={siteArea} placeholder="e.g. 2500" onChange={e => { if (onlyDecimal(e.target.value)) { setSiteArea(e.target.value); setIsDirty(true); } }} />
              </div>
              <div className="form-group">
                <label className="field-label">Average Grade</label>
                <input type="number" value={averageGrade} step="0.1" placeholder="e.g. 2.5" onChange={e => { if (onlyDecimal(e.target.value)) { setAverageGrade(e.target.value); setIsDirty(true); } }} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">Building Height ({linearLabel})</label>
                <input type="number" value={buildingHeight} step="0.1" placeholder="e.g. 12" onChange={e => { if (onlyDecimal(e.target.value)) { setBuildingHeight(e.target.value); setIsDirty(true); } }} />
              </div>
              <div className="form-group">
                <label className="field-label">Adjacent Streets <span className="required-star">*</span></label>
                <input type="number" value={adjacentStreets} min="1" placeholder="e.g. 2" onChange={e => { if (onlyDigits(e.target.value)) { setAdjacentStreets(e.target.value); setIsDirty(true); } }} />
                <div className="field-hint">Affects permitted building area thresholds under 3.2.2.10</div>
              </div>
            </div>
            <div className="field-divider" />
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">High Building</label>
                <ToggleGroup value={highBuilding ? 'yes' : 'no'} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} onChange={v => { setHighBuilding(v === 'yes'); setIsDirty(true); }} />
                <div className="field-hint">Buildings exceeding 36 m — 3.2.6 high-rise provisions</div>
              </div>
              <div className="form-group">
                <label className="field-label">Rough Construction Budget ($) <span className="optional">(optional)</span></label>
                <input type="text" value={formatWithCommas(roughConstructionBudget)} placeholder="e.g. 1,250,000"
                  onChange={e => { const raw = removeCommas(e.target.value); if (onlyDigits(raw)) { setRoughConstructionBudget(raw); setIsDirty(true); } }} />
              </div>
            </div>
          </section>
          )}

          {/* 5. Construction Type */}
          {isFieldVisible(categoryProfile, 'constructionType') && (
          <section id="construction-type" className="settings-card">
            <div className="card-header">
              <div className="card-title">Construction Type</div>
              <div className="card-desc">Scopes building code extraction to clauses applicable to your structural system. Required before extraction runs on any building code document.</div>
            </div>
            <div className="form-group">
              <label className="field-label">Construction Type <span className="required-star">*</span></label>
              <StandardDropdown value={constructionType} placeholder="Select type"
                options={CONSTRUCTION_LIST.map(([key, title]) => ({ value: key, label: title }))}
                onChange={handleCtChange} />
              {!constructionType && (
                <div className="ct-warning">
                  <svg className="ct-warning-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <div className="ct-warning-body">
                    Construction type is required for accurate building code extraction. Set this before linking a building code document.
                  </div>
                </div>
              )}
              {constructionType && CONSTRUCTION_LIST.find(([key]) => key === constructionType) && (
                <div className="ct-desc-hint">{CONSTRUCTION_LIST.find(([key]) => key === constructionType)[2]}</div>
              )}
            </div>
          </section>
          )}

          {isFieldVisible(categoryProfile, 'materialContext') && (
          <section id="infra-compliance" className="settings-card">
            <div className="card-header">
              <div className="card-title">Site Compliance</div>
              <div className="card-desc">Material context and environmental constraints for civil / site infrastructure elements.</div>
            </div>
            <div className="form-group">
              <label className="field-label">Material Context <span className="required-star">*</span></label>
              <StandardDropdown
                value={entityMeta?.materialContext || ''}
                placeholder="Select material context"
                options={MATERIAL_CONTEXT_OPTIONS}
                onChange={v => { setEntityMeta(prev => ({ ...prev, materialContext: v || '' })); setIsDirty(true); }}
              />
            </div>
            {isFieldVisible(categoryProfile, 'environmental') && (
              <div className="form-row">
                <div className="form-group">
                  <label className="field-label">Floodplain / Environmental Constraints</label>
                  <ToggleGroup
                    value={entityMeta?.environmentalFloodplain || 'unknown'}
                    options={YES_NO_UNKNOWN}
                    onChange={v => { setEntityMeta(prev => ({ ...prev, environmentalFloodplain: v })); setIsDirty(true); }}
                  />
                </div>
                <div className="form-group">
                  <label className="field-label">Stormwater Management Criteria</label>
                  <ToggleGroup
                    value={entityMeta?.environmentalSwm || 'unknown'}
                    options={YES_NO_UNKNOWN}
                    onChange={v => { setEntityMeta(prev => ({ ...prev, environmentalSwm: v })); setIsDirty(true); }}
                  />
                </div>
              </div>
            )}
          </section>
          )}

          {/* 6. Compliance Context */}
          {isFieldVisible(categoryProfile, 'compliance') && (
          <section id="compliance-context" className="settings-card">
            <div className="card-header">
              <div className="card-title">Compliance Context</div>
              <div className="card-desc">Declare based on your project program and code review. CODiii Research can help you reason through applicability. These fields scope what the compliance scanner checks against.</div>
            </div>
            <div className="form-group">
              <label className="field-label">Sprinkler System</label>
              <StandardDropdown value={sprinklerSystem} placeholder="Unknown" options={SPRINKLER_OPTIONS} onChange={v => { setSprinklerSystem(v || ''); setIsDirty(true); }} />
              <div className="field-hint">When unknown, both sprinklered and non-sprinklered requirement branches are extracted</div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="field-label">Fire Alarm</label>
                <ToggleGroup value={fireAlarm || 'unknown'} options={YES_NO_UNKNOWN} onChange={v => { setFireAlarm(v); setIsDirty(true); }}/>
              </div>
              <div className="form-group">
                <label className="field-label">Standpipe</label>
                <ToggleGroup value={standpipe || 'unknown'} options={YES_NO_UNKNOWN} onChange={v => { setStandpipe(v); setIsDirty(true); }} />
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Hazardous Substances</label>
              <ToggleGroup value={hazardousSubstances ? 'yes' : 'no'} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} onChange={v => { setHazardousSubstances(v === 'yes'); setIsDirty(true); }} />
              <div className="field-hint">Group H hazardous material provisions are included when "Yes" is declared</div>
            </div>
            {isFieldVisible(categoryProfile, 'disciplines') && (
              <div className="form-group">
                <label className="field-label">Disciplines in Scope</label>
                <div className="field-hint" style={{ marginBottom: '0.5rem' }}>
                  Optional — limit compliance and research scope to selected disciplines for this building.
                  {entityMeta?.disciplines?.length ? ` Selected: ${formatEntityDisciplinesLabel(entityMeta.disciplines)}` : ' None selected = all disciplines.'}
                </div>
                <div className="entity-discipline-checks">
                  {ENTITY_DISCIPLINE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="entity-discipline-check">
                      <input
                        type="checkbox"
                        checked={(entityMeta?.disciplines || []).includes(opt.value)}
                        onChange={() => toggleEntityDiscipline(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </section>
          )}

          {entities.length > 1 && (
            <section id="project-documents" className="settings-card">
              <div className="card-header">
                <div className="card-title">Document Tags</div>
                <div className="card-desc">Assign classified documents to a specific building, or leave as site-wide (all buildings).</div>
              </div>
              {docsLoading && <div style={{ color: '#666', fontSize: 14, padding: '8px 0' }}>Loading documents…</div>}
              {docsError && <div style={{ color: '#c62828', fontSize: 14, padding: '8px 0' }}>Failed to load classified documents.</div>}
              {!docsLoading && !docsError && classifiedDocs.length === 0 && (
                <div className="field-hint">No classified documents yet. Tags can be set during folder classification or here after indexing.</div>
              )}
              {!docsLoading && classifiedDocs.map((doc) => {
                const tagIds = doc.projectBuildingDataIds?.length
                  ? doc.projectBuildingDataIds
                  : (documentTags[doc.id] || []);
                return (
                  <div key={doc.id} className="doc-tag-row">
                    <div className="doc-tag-info">
                      <div className="doc-tag-name" title={doc.fileName}>{doc.fileName}</div>
                      <div className="doc-tag-meta">
                        {doc.category}{doc.complianceType ? ` · ${doc.complianceType}` : ''}
                        {tagIds.length > 0 && (
                          <span className="doc-tag-applies"> · {formatAppliesToLabel(tagIds, entities)}</span>
                        )}
                      </div>
                    </div>
                    <div className="doc-tag-checks">
                      {entities.map((ent) => (
                        <label key={ent.id} className="doc-tag-check">
                          <input
                            type="checkbox"
                            checked={tagIds.includes(ent.id)}
                            onChange={() => toggleDocEntityTag(doc.id, ent.id, tagIds)}
                          />
                          {ent.name || ent.id}
                        </label>
                      ))}
                      {tagIds.length > 0 && (
                        <button type="button" className="doc-tag-clear" onClick={() => handleDocTagChange(doc.id, [])}>
                          Site-wide
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          {/* Team & Access */}
          {planHasTeamAccess(planKey) && (
            <section id="team" className="settings-card">
              <div className="card-header">
                <div className="card-title">Team &amp; Access</div>
                <div className="card-desc">Manage who can view and edit this project</div>
              </div>
              {teamLoading && <div style={{ color: '#666', fontSize: 14, padding: '8px 0' }}>Loading team…</div>}
              {teamError   && <div style={{ color: '#c62828', fontSize: 14, padding: '8px 0' }}>Failed to load team.</div>}
              {!teamLoading && !teamError && projectMembers.map(m => {
                const cm = m?.userId ? companyMemberByUserId.get(m.userId) : null;
                const email = cm?.email || '(unknown user)';
                const role  = m?.role || 'MEMBER';
                return (
                  <div key={m.id} className="team-row">
                    <div className="member-left">
                      <div className="avatar">{(email || 'U').slice(0, 1).toUpperCase()}</div>
                      <div><div className="member-name">{email}</div><div className="member-email">{email}</div></div>
                    </div>
                    <div className="member-right">
                      <span className={`role-badge${role === 'OWNER' ? ' owner' : ''}`}>{role}</span>
                      {canManageTeam && role !== 'OWNER' && <button className="btn-remove" onClick={() => handleRemoveMember(m)}>Remove</button>}
                    </div>
                  </div>
                );
              })}
              {canManageTeam && <button className="btn-add-member" onClick={() => setAddOpen(true)}>+ Add Team Member</button>}
            </section>
          )}

          {/* Danger Zone */}
          {canDeleteProject && (
            <div className="danger-zone">
              <div className="danger-zone-copy">
                <div className="danger-zone-title">Delete Project</div>
                <div className="danger-zone-desc">Permanently deletes this project and all associated data including linked documents, requirements, and compliance history. This action cannot be undone.</div>
              </div>
              <button className="btn btn-danger-ghost" onClick={handleDeleteProject} disabled={loading}>Delete Project</button>
            </div>
          )}

        </div>{/* /cards-column */}
      </div>{/* /page-body */}

      {/* ── Add Member Modal ── */}
      {planHasTeamAccess(planKey) && canManageTeam && addOpen && (
        <div className="add-member-modal-backdrop" onClick={() => setAddOpen(false)}>
          <div className="add-member-modal" onClick={e => e.stopPropagation()}>
            <div className="add-member-modal-header">
              <div className="add-member-modal-title">Add Team Member</div>
              <button className="add-member-modal-close" onClick={() => setAddOpen(false)}>×</button>
            </div>
            <div className="form-group">
              <label className="field-label">Select member</label>
              <StandardDropdown value={selectedUserId} placeholder="Select…"
                options={availableCompanyMembers.map(cm => {
                  const hasUserId        = !!cm.userId;
                  const alreadyInProject = hasUserId && projectMemberUserIds.has(cm.userId);
                  return { value: hasUserId ? cm.userId : `disabled-${cm.id}`, label: `${cm.email}${!hasUserId ? ' (invite not accepted yet)' : ''}${alreadyInProject ? ' (already in project)' : ''}`, disabled: !hasUserId || alreadyInProject };
                })}
                onChange={v => setSelectedUserId(v || '')} />
            </div>
            <div className="form-group">
              <label className="field-label">Role</label>
              <StandardDropdown value={selectedRole} options={TEAM_ROLE_OPTIONS} onChange={v => setSelectedRole(v || 'MEMBER')} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button className="btn btn-secondary" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddMember}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Construction Type Warning Modal ── */}
      {ctWarningOpen && (
        <div className="ct-modal-backdrop" onClick={() => setCtWarningOpen(false)}>
          <div className="ct-modal" onClick={e => e.stopPropagation()}>
            <div className="ct-modal-body">
              <div className="ct-modal-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className="ct-modal-content">
                <div className="ct-modal-title">Change Construction Type?</div>
                <div className="ct-modal-message">
                  All building code requirements currently in your Requirements Register will be marked as stale and re-filtered using the new construction type. This runs quickly in the background.
                  <br /><br />
                  Existing Compliance Issues linked to building code requirements will be marked as stale. You may need to re-run compliance scans after re-filtering is complete.
                  <br /><br />
                  Verification history for building code requirements is retained as an audit trail but will no longer reflect active requirements.
                </div>
                <label className="ct-modal-confirm-label">
                  Type <strong style={{ textTransform: 'none' }}>
                    {CONSTRUCTION_LIST.find(([key]) => key === pendingCtType)?.[1] || pendingCtType}
                  </strong> to confirm
                </label>
                <input className="ct-modal-confirm-input" type="text" value={ctConfirmText}
                  onChange={e => setCtConfirmText(e.target.value)} placeholder="Type construction type name to confirm"
                  onKeyDown={e => { if (e.key === 'Enter' && ctConfirmText.trim()) handleCtConfirm(); }} autoFocus />
              </div>
            </div>
            <div className="ct-modal-footer">
              <button className="btn btn-secondary" onClick={() => { setCtWarningOpen(false); setCtConfirmText(''); setPendingCtType(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCtConfirm} disabled={!ctConfirmText.trim()}
                style={!ctConfirmText.trim() ? { background: '#ccc', cursor: 'not-allowed' } : {}}>
                Change Construction Type
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}